/**
 * Shared lifecycle for the WebGL decorations.
 *
 * Every scene on the site wants the same things: don't load three.js until the
 * object is near the viewport, don't run at all on small screens or under
 * reduced-motion, pause when off-screen or backgrounded, and dispose
 * everything on unmount. That logic lives here once so the individual scenes
 * are just geometry and lighting.
 */

const MOBILE_BREAKPOINT = 768

/**
 * Colours sampled from the 3D logo.
 *
 * `pewter` replaces the logo's charcoal body tone for geometry. #181818 is
 * correct for the logo, where it sits on a light surround, but on this site's
 * near-black page a charcoal solid is invisible — it reads as a hole with a
 * shadow under it. #6e6e6e is also taken from the logo (its mid-grey bevel)
 * and actually separates from the background.
 */
export const BRAND = {
  pewter: 0x6e6e6e,
  gold: 0xd8a848,
  goldBright: 0xffd118,
  cream: 0xf0f0d8
}

function viewportWidth() {
  // innerWidth reads 0 in some embedded and headless contexts; a bogus 0 must
  // not be mistaken for a phone.
  const w = Math.max(window.innerWidth || 0, document.documentElement?.clientWidth || 0)
  return w || MOBILE_BREAKPOINT
}

export function canRunWebgl() {
  if (typeof window === 'undefined') return false
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false
  if (viewportWidth() < MOBILE_BREAKPOINT) return false
  try {
    const c = document.createElement('canvas')
    return !!(c.getContext('webgl2') || c.getContext('webgl'))
  } catch {
    return false
  }
}

/**
 * Mount a scene into `host`.
 *
 * `build({ THREE, scene, camera, width, height })` adds geometry and lights and
 * returns `{ update, dispose }`. `update(t)` runs each frame with elapsed
 * seconds; `dispose()` frees whatever it created.
 *
 * Returns a teardown function. Safe to call before anything has loaded.
 */
export function mountThreeScene(host, build, { onReady } = {}) {
  if (!host || !canRunWebgl()) return () => {}

  let disposed = false
  let teardown = null
  let started = false

  const start = async () => {
    let THREE
    try {
      THREE = await import('three')
    } catch {
      return // caller's fallback stays visible
    }
    if (disposed || !host.isConnected) return

    const width = host.clientWidth || 640
    const height = host.clientHeight || 520

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'low-power'
    })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(width, height, false)
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFSoftShadowMap
    const el = renderer.domElement
    el.style.display = 'block'
    el.style.width = '100%'
    el.style.height = '100%'
    host.appendChild(el)

    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(34, width / height, 0.1, 100)

    // A metal is almost entirely reflection — it has next to no diffuse term.
    // With `metalness` near 1 and nothing in the environment, every metallic
    // surface reflects an empty black scene and renders muddy brown instead of
    // gold. RoomEnvironment is generated in code (no texture to download) and
    // gives those surfaces something to pick up.
    let pmrem
    try {
      const { RoomEnvironment } = await import('three/examples/jsm/environments/RoomEnvironment.js')
      pmrem = new THREE.PMREMGenerator(renderer)
      pmrem.compileEquirectangularShader()
      const room = new RoomEnvironment()
      scene.environment = pmrem.fromScene(room, 0.04).texture
      room.dispose?.()
    } catch {
      // Without it the lights still work; the metals just read flatter.
    }

    // Physically-correct tone mapping, so the gold highlights roll off instead
    // of clipping to white.
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.15
    if ('outputColorSpace' in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace

    const built = build({ THREE, scene, camera, width, height }) || {}

    let pointerX = 0
    let pointerY = 0
    const onPointer = (e) => {
      const r = host.getBoundingClientRect()
      pointerX = ((e.clientX - r.left) / r.width - 0.5) * 2
      pointerY = ((e.clientY - r.top) / r.height - 0.5) * 2
    }
    window.addEventListener('pointermove', onPointer, { passive: true })

    const onResize = () => {
      const w = host.clientWidth || width
      const h = host.clientHeight || height
      renderer.setSize(w, h, false)
      camera.aspect = w / h
      camera.updateProjectionMatrix()
    }
    window.addEventListener('resize', onResize)

    let visible = true
    let io
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver((es) => { visible = es[0]?.isIntersecting ?? true }, { threshold: 0.02 })
      io.observe(host)
    }
    const onVis = () => { visible = document.visibilityState === 'visible' }
    document.addEventListener('visibilitychange', onVis)

    let raf = 0
    let t = 0
    const tick = () => {
      raf = requestAnimationFrame(tick)
      if (!visible) return
      t += 0.0045
      built.update?.(t, pointerX, pointerY)
      renderer.render(scene, camera)
    }
    tick()

    onReady?.()

    teardown = () => {
      cancelAnimationFrame(raf)
      io?.disconnect()
      window.removeEventListener('pointermove', onPointer)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('visibilitychange', onVis)
      built.dispose?.()
      scene.environment?.dispose?.()
      pmrem?.dispose()
      renderer.dispose()
      if (el.parentNode === host) host.removeChild(el)
    }
  }

  // Defer until near the viewport. An IntersectionObserver alone never fires
  // in a hidden or embedded tab, so a rect check on scroll backs it up.
  const nearViewport = () => {
    const r = host.getBoundingClientRect()
    const vh = window.innerHeight || document.documentElement.clientHeight || 0
    return r.top < vh + 250 && r.bottom > -250
  }

  const begin = () => {
    if (started) return
    started = true
    loadIo?.disconnect()
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onScroll)
    start()
  }

  const onScroll = () => { if (nearViewport()) begin() }

  let loadIo
  if (typeof IntersectionObserver !== 'undefined') {
    loadIo = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) begin()
    }, { rootMargin: '250px' })
    loadIo.observe(host)
  }
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll)
  if (nearViewport()) begin()

  return () => {
    disposed = true
    loadIo?.disconnect()
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onScroll)
    teardown?.()
  }
}
